import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortable, MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SsoDomain } from '../../../core/models/SsoDomain';
import { MOCK_DOMAINS } from './mock/mock-domains';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SsoDomainService } from '../../../core/services/sso-domain.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AddDomainDialogComponent } from '../../../shared/components/add-domain-dialog/add-domain-dialog.component';
import { EditDomainDialogComponent } from '../../../shared/components/edit-domain-dialog/edit-domain-dialog.component';
import { LoadingService } from '../../../core/services/loading.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';


@Component({
  selector: 'app-sso-domain-management',
  imports: [
    MatTableModule, 
    MatPaginatorModule, 
    MatSortModule, 
    MatInputModule, 
    MatFormFieldModule, 
    MatButtonModule,
    MatIconModule,
    MatTooltipModule, 
    MatDialogModule,
    MatProgressSpinnerModule,
    AsyncPipe,
    LoadingSpinnerComponent
  ],
  templateUrl: './sso-domain-management.component.html',
  styleUrl: './sso-domain-management.component.css',
})
export class SsoDomainManagementComponent implements OnInit {
  @ViewChild('input') searchInput!: ElementRef;
  private ssoDomainService = inject(SsoDomainService);
  loadingService = inject(LoadingService);
  displayedColumns: string[] = ['ssoDomainValue', 'numberOfUsers', "Actions"];
  dataSource: MatTableDataSource<SsoDomain>;
  private readonly PAGE_SIZE_KEY = 'sso-domain-table-page-size';
  
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(private dialog: MatDialog) {
    this.dataSource = new MatTableDataSource<SsoDomain>([]);
  }

  ngOnInit() {
    // This avoids unnecessary reloading when the component is reused
    if (!this.dataSource.data.length) {
      this.loadDomains();
    }
    
    // Load saved page size from localStorage if available
    this.loadSavedPageSize();
    
    this.setupWildcardSearchFilter();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    
    // setTimeout defers the sort initialization to the next change detection cycle
    setTimeout(() => {
      this.sort.sort({ id: 'ssoDomainValue', start: 'asc' } as MatSortable);
    });
    
    // Add page size change listener
    this.paginator.page.subscribe(() => {
      this.savePageSize(this.paginator.pageSize);
    });
  }


  loadDomains() {
    console.log('Loading SSO domains table...');

    //this.dataSource.data = MOCK_DOMAINS;
    this.ssoDomainService.getSsoDomains().subscribe({
      next: (domains) => {
        console.table(domains);
        this.dataSource.data = domains;
      },
      error: (error) => {
        console.error('Error loading domains: ', error);
      },
    });
  }

  reloadTable() {
    if (this.loadingService.isLoading()) {
      console.log('Table is already reloading');
      return;
    } 

    if (this.dataSource.data.length > 0) {
      this.dataSource = new MatTableDataSource<SsoDomain>([]);
    }

    this.loadDomains();

    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  openAddDomainDialog() {
    const dialogRef = this.dialog.open(AddDomainDialogComponent, {
      width: '600px',
      disableClose: true,
    });
  
    dialogRef.beforeClosed().subscribe(result => {
      console.log('result:', result);
      if (result) {
        console.log('Domain added:', result);
        // Update the data source with the new domain
        const currentData = [...this.dataSource.data];
        currentData.push(result);
        this.dataSource.data = currentData;

        this.dataSource._updateChangeSubscription();
      }
    });
  }

  openEditDomainDialog(element: SsoDomain) {
    const dialogRef = this.dialog.open(EditDomainDialogComponent, {
      width: '600px',
      disableClose: true,
      data: element
    });

    dialogRef.beforeClosed().subscribe(result => {
      if (result) {
        console.log('Domain edited:', result);
        // Find and update the edited domain in the data source
        const currentData = [...this.dataSource.data];
        const index = currentData.findIndex(d => d.ssoDomainId === element.ssoDomainId);
        if (index !== -1) {
          currentData[index] = result;
          this.dataSource.data = currentData;
          this.dataSource._updateChangeSubscription();
        }
      }
    });
  }

  private loadSavedPageSize() {
    const savedPageSize = localStorage.getItem(this.PAGE_SIZE_KEY);
    if (savedPageSize) {
      // setTimeout waits for AfterViewInit when paginator is available
      setTimeout(() => {
        this.paginator.pageSize = parseInt(savedPageSize, 10);
      });
    }
  }
  
  private savePageSize(pageSize: number) {
    localStorage.setItem(this.PAGE_SIZE_KEY, pageSize.toString());
  }
  
  removeSearch() {
    // This clears the search input value
    this.searchInput.nativeElement.value = '';
    this.dataSource.filter = '';
  }

private setupWildcardSearchFilter() {
  this.dataSource.filterPredicate = (data: SsoDomain, filter: string) => {
    if (!filter) return true;
    
    if (filter.includes('*') || filter.includes('?')) {
      // Escape special regex characters except for wildcards
      const escapedFilter = filter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                                 .replace(/\\\*/g, '.*')
                                 .replace(/\\\?/g, '.');
      
      try {
        const regex = new RegExp(`^${escapedFilter}$`, 'i');
        return regex.test(data.ssoDomainValue);
      } catch (e) {
        return false;
      }
    }
    
    // Use default filter for non-wildcard searches
    return data.ssoDomainValue.toLowerCase().includes(filter.toLowerCase());
  };
  }
}
